import { EmblaCarouselType } from "embla-carousel";
import { useEffect, useState, RefObject } from "react";

export const useLazyLoadingCarouselImages = (
  ref: RefObject<HTMLDivElement | null>,
  images: string[],
  api?: EmblaCarouselType,
  isInitialyDisplayed = false,
  loadTwoImages = false,
) => {
  const [isVisible, setIsVisible] = useState(isInitialyDisplayed);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(
    isInitialyDisplayed ? new Set([images[0]]) : new Set(),
  );

  useEffect(() => {
    if (!api) return;

    const updateSlidesInView = () => {
      const slidesInView = api.slidesInView();

      setLoadedImages((prevLoaded) => {
        const newLoaded = new Set(prevLoaded);
        // Add current slide and adjacent slides
        slidesInView.forEach((index) => {
          newLoaded.add(images[index]);
        });
        return newLoaded;
      });
    };

    api.on("slidesInView", updateSlidesInView);

    return () => {
      api.off("slidesInView", updateSlidesInView);
    };
  }, [api]);

  useEffect(() => {
    if (!isInitialyDisplayed) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (images.length > 1 && loadTwoImages) {
              setLoadedImages(new Set([images[0], images[1]]));
            } else {
              setLoadedImages(new Set([images[0]]));
            }
            observer.disconnect();
          }
        },
        { rootMargin: "50px" },
      );

      if (ref.current) {
        observer.observe(ref.current);
      }

      return () => observer.disconnect();
    }
  }, []);

  return { isVisible, loadedImages };
};
